import i18n from './i18n';
import './with.css';

import React from 'react';
import { createRoot } from 'react-dom/client';

import { App } from './App';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

let version: string;
try {
  version = `with-app@${typeof VERSION !== 'undefined' ? VERSION : 'dev'}`;
} catch (err) {
  version = 'unknown';
}

(window as any)[`__version__`] = version;

// injectFirst on the styles provider tells material ui to inject our styles before the
// base classes so we can avoid having to put !important flags on all of our positional css
// https://material-ui.com/guides/interoperability/#controlling-priority-4

// Wrapper component to handle service worker hooks properly
const AppWithServiceWorker: React.FC = () => {
  React.useEffect(() => {
    // Dynamic import to avoid calling hooks in global scope
    import('./features/updates/useUpdatesStore').then(({ useUpdateStore }) => {
      serviceWorkerRegistration.register({
        onUpdate: (registration) => {
          // subscribe to a change in the updateAccepted flag,
          // which will indicate the user has requested to update to the latest
          // app version immediately
          useUpdateStore.subscribe<boolean>(
            (accepted) => {
              if (!accepted) return;

              // posting this message will promote the waiting service worker to become
              // active immediately.
              registration.waiting?.postMessage({
                type: 'SKIP_WAITING',
              });
            },
            (store) => store.updateAccepted
          );
          useUpdateStore.getState().api.onUpdateReady();
        },
      });
    });
  }, []);

  React.useEffect(() => {
    const applyDocumentLanguage = (language: string) => {
      const normalized = (language || 'en').split('-')[0];
      document.documentElement.lang = normalized;
      document.documentElement.dir = normalized === 'ar' ? 'rtl' : 'ltr';
    };

    applyDocumentLanguage(i18n.language);
    i18n.on('languageChanged', applyDocumentLanguage);

    return () => {
      i18n.off('languageChanged', applyDocumentLanguage);
    };
  }, []);

  return <App />;
};

// React 18 createRoot API
const container = document.getElementById('root');
const root = createRoot(container!);
root.render(<AppWithServiceWorker />);
